<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useApi } from '@/utils/axios'
import { PEDIDO_ROTAS, USUARIO_ROTAS } from '@/utils/apiRoutes'

const apiHistorico = useApi(PEDIDO_ROTAS.PEDIDO_PENDENTE_HISTORICO)
const apiUsuarios = useApi(USUARIO_ROTAS.USUARIOS)

// --- Filtros ---
const datainicio = ref('')
const datafinal = ref('')
const funcionario = ref(null)
const statusFiltro = ref(null)
const idpedidoFiltro = ref('')

const statusOptions = [
    { title: 'Marcou', value: 'M' },
    { title: 'Desmarcou', value: 'D' },
]

// --- Usuários (atendentes) ---
const usuariosOptions = ref([])
const loadingUsuarios = ref(false)

let debounceUsuarios = null

async function carregarUsuarios(name = '') {
    loadingUsuarios.value = true
    try {
        const res = await apiUsuarios.fetch({ params: { page: 1, per_page: 100, name: name || undefined } })
        const lista = res?.data ?? res ?? []
        usuariosOptions.value = lista.map((u) => ({ title: u.nome, value: u.id }))
    } catch {
        // silencioso
    } finally {
        loadingUsuarios.value = false
    }
}

function onSearchAtendente(val) {
    clearTimeout(debounceUsuarios)
    debounceUsuarios = setTimeout(() => carregarUsuarios(val ?? ''), 400)
}

// --- Paginação ---
const page = ref(1)
const perPage = ref(50)
const total = ref(0)
const lastPage = ref(1)

const totalPages = computed(() => lastPage.value)

// --- Dados ---
const items = ref([])
const loading = apiHistorico.loading

const headers = [
    { title: 'ID', value: 'id', width: '80px', sortable: true },
    { title: 'ID Pedido', value: 'idpedido', width: '110px', sortable: true },
    { title: 'Ação', value: 'acao', width: '120px', sortable: true },
    { title: 'Atendente', value: 'nome', sortable: true },
    { title: 'Data/Hora', value: 'criado_em', width: '180px', sortable: true },
]

async function buscar() {
    const params = {
        datainicio: datainicio.value || undefined,
        datafinal: datafinal.value || undefined,
        funcionario: funcionario.value || undefined,
        status: statusFiltro.value || undefined,
        idpedido: idpedidoFiltro.value || undefined,
        per_page: perPage.value,
        page: page.value,
    }

    try {
        const res = await apiHistorico.fetch({ params })
        const paginator = res?.data ?? {}
        items.value = paginator.data ?? []
        total.value = paginator.total ?? 0
        lastPage.value = paginator.last_page ?? 1
        page.value = paginator.current_page ?? 1
    } catch {
        // tratado pelo useApi
    }
}

function limparFiltros() {
    datainicio.value = ''
    datafinal.value = ''
    funcionario.value = null
    statusFiltro.value = null
    idpedidoFiltro.value = ''
    page.value = 1
    buscar()
}

function onPageChange(newPage) {
    page.value = newPage
    buscar()
}

function onPerPageChange() {
    page.value = 1
    buscar()
}

// Ao alterar filtros de data/atendente/status, resetar para página 1
watch([datainicio, datafinal, funcionario, statusFiltro, idpedidoFiltro], () => {
    page.value = 1
    buscar()
})

onMounted(() => {
    carregarUsuarios('')
})
</script>

<template>
    <div>
        <v-row>
            <v-col cols="12" class="d-flex align-center">
                <h1 class="text-h4">Pendentes - Histórico</h1>
            </v-col>
        </v-row>

        <!-- Filtros -->
        <v-card class="pa-4 mb-4">
            <v-row align="center">
                <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="datainicio" type="date" label="Data início" density="compact" hide-details
                        clearable prepend-inner-icon="mdi-calendar-start" />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="datafinal" type="date" label="Data fim" density="compact" hide-details
                        clearable prepend-inner-icon="mdi-calendar-end" :min="datainicio" />
                </v-col>

                <v-col cols="12" sm="6" md="3">
                    <v-autocomplete v-model="funcionario" :items="usuariosOptions" :loading="loadingUsuarios"
                        item-title="title" item-value="value" label="Atendente" density="compact" hide-details clearable
                        prepend-inner-icon="mdi-account" no-data-text="Nenhum atendente encontrado"
                        @update:search="onSearchAtendente" />
                </v-col>
            </v-row>

            <v-row align="center">
                <v-col cols="12" sm="6" md="3">
                    <v-select v-model="statusFiltro" :items="statusOptions" label="Ação" density="compact" hide-details
                        clearable prepend-inner-icon="mdi-filter" />
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-text-field v-model="idpedidoFiltro" label="ID Pedido" density="compact" hide-details clearable
                        prepend-inner-icon="mdi-numeric" />
                </v-col>


            </v-row>

            <v-row>
                <v-col cols="12" class="d-flex ga-2">
                    <v-btn color="primary" @click="buscar">
                        Buscar
                        <v-icon>mdi-magnify</v-icon>
                    </v-btn>
                    <v-btn text @click="limparFiltros">Limpar</v-btn>
                </v-col>
            </v-row>
        </v-card>

        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />

        <v-data-table :headers="headers" :items="items" :loading="loading" hide-default-footer density="comfortable"
            :items-per-page="-1">
            <template #item.nome="{ item }">
                {{ item.nome ?? '—' }}
            </template>

            <template #item.acao="{ item }">
                <v-chip style="font-weight: bold; text-transform: uppercase;"
                    :color="item.acao === 'Marcou' ? 'warning' : 'success'" size="small" label>
                    {{ item.acao }}
                </v-chip>
            </template>

            <template #bottom>
                <div class="d-flex align-center justify-space-between pa-3 flex-wrap gap-2">
                    <div class="text-body-2 text-medium-emphasis">
                        Total: {{ total }} registro{{ total !== 1 ? 's' : '' }}
                    </div>

                    <div class="d-flex align-center gap-3">
                        <v-select v-model="perPage" :items="[10, 25, 50, 100]" label="Por página" density="compact"
                            hide-details style="width: 120px" @update:model-value="onPerPageChange" />

                        <v-pagination v-if="totalPages > 1" :model-value="page" :length="totalPages" :total-visible="5"
                            rounded @update:model-value="onPageChange" />
                    </div>
                </div>
            </template>

            <template #no-data>
                <div class="text-center py-6 text-medium-emphasis">
                    Nenhum registro encontrado.
                </div>
            </template>
        </v-data-table>
    </div>
</template>
